import { useEffect, useId, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal } from '../Modal';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { subscribeWixelEditChoice, type WixelEditChoiceRequest } from '../../lib/wixel-edit-choice';
import { Button, Typography } from './ui';

// The "Edit in Wixel" choice on a phone or tablet browser: get the Wixel app, or edit in the browser. Raised
// by `requestWixelEditChoice` (`lib/wixel-edit-choice.ts`, which owns the when and the where-to) and mounted
// ONCE in `_app.tsx`, so every host reaches the same sheet without wiring it.
//
// Not `useConfirm`: a confirm answers yes or no, and its ✕ / Escape / scrim answer "no". Here dismissing
// must open NEITHER link, and both buttons are actions, so there is no "no" to map it to.
//
// Both buttons are real anchors opening a new tab, not handlers that navigate afterwards: the tap itself is
// the navigation, so no popup blocker (mobile Safari's in particular) can eat it.
// "Get the Wixel app" is the app's universal link, which opens the installed
// app or redirects to its store, or the App Store / Play page for this OS when there is no app link.
export function WixelEditChoiceSheet() {
  const { t } = useTranslation();
  const { pathname } = useRouter();
  const { logModalActionBi } = useGenericBi();
  const titleId = useId();
  const [request, setRequest] = useState<WixelEditChoiceRequest | null>(null);

  useEffect(() => subscribeWixelEditChoice(setRequest), []);

  if (!request) return null;

  const answer = (action: 'get_app' | 'in_browser' | 'close') => {
    logModalActionBi({ page_name: pathname, modal_name: 'wixel_edit_choice', action });
    setRequest(null);
  };

  return (
    <Modal
      open
      onClose={() => answer('close')}
      presentation="sheet"
      title={t('wixelEditChoice.title')}
      labelledBy={titleId}
      footer={
        <>
          <Button variant="outlined" href={request.editorUrl} external target="_blank" rel="noopener noreferrer" onClick={() => answer('in_browser')}>
            {t('wixelEditChoice.inBrowser')}
          </Button>
          <Button color="accent" href={request.getAppUrl} external target="_blank" rel="noopener noreferrer" onClick={() => answer('get_app')}>
            {t('wixelEditChoice.getApp')}
          </Button>
        </>
      }
    >
      <Typography variant="p2" color="secondary" component="p">
        {t('wixelEditChoice.body')}
      </Typography>
    </Modal>
  );
}
